import { Mail, AlertTriangle } from 'lucide-react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * ReplyAsSelector — the 0 / 1 / many sender picker, shared by both email lanes.
 *
 * The departmental lane (ComposePage, EmailDetailPage) builds its options with
 * `inboxRules.fromOptions`; the personal lane (issue #484) builds them with
 * `hooks/usePersonalSenders`. Every prop added for the personal lane defaults
 * to today's behaviour, so the departmental callers are byte-identical.
 *
 * @param {Array<{address:string, kind?:string, value?:string, sendable?:boolean}>} options
 * @param {string} [singleHint] - copy under the fixed one-option display. The
 *   default is departmental-lane wording ("…for this inbox"); the personal lane
 *   passes its own.
 * @param {(opt) => React.ReactNode} [renderBadge] - optional per-option
 *   adornment, e.g. the Gmail/Outlook provider pill. Departmental callers pass
 *   nothing.
 * @param {'stacked'|'inline'} [variant] - `stacked` (the default) draws its own
 *   label above a bordered box (the contact-record composer). `inline`
 *   draws the control alone, borderless, for a surface that already supplies
 *   the label in a row gutter — the full-page composer, where the design reads
 *   "From   addr ⌄" on one hairline row rather than as a form field. The
 *   single-option hint and the label are the row's job there, so `inline` drops
 *   both rather than repeating them (the label stays as the accessible name).
 * @param {'md'|'sm'} [size] - `inline` only. `sm` is the reader footer's
 *   11.5px muted line (#659), so the picker reads as part of "Replying as …"
 *   rather than as a louder control beside it.
 * @param {boolean} [disabled] - disables the picker (inline and stacked).
 */
export default function ReplyAsSelector({
    options,
    value,
    onChange,
    // Every caller passes its own; "From" is the neutral default.
    label = 'From',
    singleHint = 'This is the only sender address available for this inbox.',
    emptyHint = 'No send address available for this inbox',
    renderBadge,
    variant = 'stacked',
    size = 'md',
    disabled = false,
}) {
    const inline = variant === 'inline';
    // One place per size difference; everything else is shared.
    const TEXT = inline && size === 'sm' ? 'text-[11.5px] text-[#57504B]' : 'text-[13.5px] text-[#0C0A09]';

    if (inline) {
        if (!options || options.length === 0) {
            return (
                <span className={`${inline && size === 'sm' ? 'text-[11.5px]' : 'text-[13.5px]'} text-[#A29B95] italic`} data-testid="reply-as-empty">
                    {emptyHint}
                </span>
            );
        }
        if (options.length === 1) {
            const opt = options[0];
            return (
                <span className="flex items-center gap-2 min-w-0" data-testid="reply-as-fixed">
                    <span className={`${TEXT} truncate`}>{opt.address}</span>
                    {renderBadge?.(opt)}
                    {opt.sendable === false && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-600">
                            <AlertTriangle className="w-3 h-3" /> Reconnect required
                        </span>
                    )}
                </span>
            );
        }
        return (
            <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled}>
                <SelectTrigger
                    data-testid="reply-as-selector"
                    // The inline variant draws no visible label (its row does),
                    // so the control still needs an accessible name.
                    aria-label={label}
                    className={`h-auto w-auto border-0 shadow-none rounded-none bg-transparent
                               px-0 py-0 gap-2 ${TEXT}
                               focus:ring-0 focus:ring-offset-0 [&>svg]:opacity-100 [&>svg]:text-[#8A837E]`}
                >
                    <SelectValue placeholder="Select sender" />
                </SelectTrigger>
                <SelectContent>
                    {options.map(opt => (
                        <SelectItem
                            key={opt.value ?? opt.address}
                            value={opt.value ?? opt.address}
                            disabled={opt.sendable === false}
                        >
                            <span className="flex items-center gap-2">
                                {opt.address}
                                {renderBadge?.(opt)}
                                {opt.kind === 'member' && ' (personal)'}
                                {opt.sendable === false && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-amber-600">
                                        <AlertTriangle className="w-3 h-3" /> Reconnect required
                                    </span>
                                )}
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    if (!options || options.length === 0) {
        return (
            <div className="space-y-1">
                <Label className="text-xs font-medium text-stone-600">{label}</Label>
                <div className="text-xs text-stone-400 italic py-2">
                    {emptyHint}
                </div>
            </div>
        );
    }

    if (options.length === 1) {
        const opt = options[0];
        return (
            <div className="space-y-1">
                <Label className="text-xs font-medium text-stone-600">{label}</Label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-stone-200 bg-white" data-testid="reply-as-fixed">
                    <Mail className="w-3.5 h-3.5 text-stone-400" />
                    <span className="text-sm text-stone-700 font-medium">{opt.address}</span>
                    {renderBadge?.(opt)}
                    {opt.kind === 'member' && !opt.pool && (
                        <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">personal</span>
                    )}
                    {opt.kind === 'member' && opt.pool && (
                        <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">pooled</span>
                    )}
                    {opt.kind === 'distro' && (
                        <span className="text-[10px] text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">shared</span>
                    )}
                    {opt.sendable === false && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-amber-600">
                            <AlertTriangle className="w-3 h-3" /> Reconnect required
                        </span>
                    )}
                </div>
                <p className="text-[10px] text-stone-400">{singleHint}</p>
            </div>
        );
    }

    // Multiple options — dropdown
    return (
        <div className="space-y-1">
            <Label className="text-xs font-medium text-stone-600">{label}</Label>
            {/* `?? undefined`: the personal lane yields a null provider when
                every connected mailbox has broken mail auth, and Radix's Select
                treats null as neither a controlled value nor "uncontrolled". */}
            <Select value={value ?? undefined} onValueChange={onChange} disabled={disabled}>
                <SelectTrigger data-testid="reply-as-selector" className="bg-white">
                    <SelectValue placeholder="Select sender" />
                </SelectTrigger>
                <SelectContent>
                    {options.map(opt => (
                        // `value` falls back to `address` so departmental callers,
                        // which never set it, behave exactly as before. The personal
                        // lane sets it to the provider id, which keeps the item
                        // values unique even when two providers report the same
                        // address (Google MX + an M365 account on one UPN).
                        <SelectItem
                            key={opt.value ?? opt.address}
                            value={opt.value ?? opt.address}
                            disabled={opt.sendable === false}
                        >
                            <span className="flex items-center gap-2">
                                {opt.address}
                                {renderBadge?.(opt)}
                                {opt.kind === 'member' && ' (personal)'}
                                {opt.sendable === false && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-amber-600">
                                        <AlertTriangle className="w-3 h-3" /> Reconnect required
                                    </span>
                                )}
                            </span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}
